<script lang="ts" setup>
import type { Scope } from '../../runtime/services/StorageService';
import ImageGeneratorService from '../../runtime/services/ImageGeneratorService';
import CropperLeftSidebar from './_partials/cropper/CropperLeftSidebar.vue';
import CropperRange from './_partials/cropper/CropperRange.vue';
import CropperRightSidebar from './_partials/cropper/CropperRightSidebar.vue';
import CropperStage from './_partials/cropper/CropperStage.vue';
import {
    cropperTemplates,
    cropperWatermarkPositionPresets
} from './runtime/cropper';
import { createCropperEditor } from './runtime/cropperEditor';

const props = withDefaults(defineProps<{
    enabled: boolean;
    selected?: FileResource;
    sourceFile?: File | null;
    scope: Scope;
    folder?: number;
    dialog?: boolean;
    actionsTarget?: string;
}>(), {
    selected: null,
    sourceFile: null,
    folder: null,
    dialog: false,
    actionsTarget: ''
});

const emit = defineEmits<{
    (e: 'error'): void;
    (e: 'open-dialog'): void;
    (e: 'local-saved', value: File): void;
    (e: 'saved', value: FileResource): void;
    (e: 'edit', value: FileResource): void;
    (e: 'cancel'): void;
}>();

const { $t } = useLocalization();
const fieldId = useId();
const { $alert } = useAlerts();
const { $log } = useLogging();

const backgroundRemovalLoading = ref(false);
const zoomTarget = `file-editor-zoom-${useId()}`;

async function removeBackground()
{
    if (!props.selected?.url)
    {
        $alert.warning($t('[[[Nie wybrano obrazu do edycji]]]'));

        return;
    }

    try
    {
        backgroundRemovalLoading.value = true;

        $alert.info($t('[[[Usuwanie tła...]]]'));

        const result = await ImageGeneratorService.removeBackground(props.selected.url);

        $alert.info($t('[[[Zapisywanie obrazu...]]]'));

        const savedImages = await ImageGeneratorService.saveImages([result], props.scope, props.folder);

        if (savedImages && savedImages.length > 0)
        {
            $alert.success($t('[[[Tło zostało usunięte i zapisane jako nowy plik]]]'));
            emit('edit', savedImages[0]);

            return;
        }

        $alert.warning($t('[[[Obraz został przetworzony ale mogły wystąpić problemy z zapisem]]]'));
    }
    catch (ex: any)
    {
        handleException($log, ex, {
            400: (error: any) => $alert.warning($t(error.message))
        });
    }
    finally
    {
        backgroundRemovalLoading.value = false;
    }
}

const {
    cropperStage,
    cropperRightSidebar,
    loading,
    saving,
    websiteLogoLoading,
    cropMode,
    aspectRatio,
    fixedSize,
    circularSize,
    flipHorizontal,
    flipVertical,
    scale,
    brightness,
    contrast,
    saturation,
    grayscale,
    watermarkEnabled,
    watermarkType,
    watermarkText,
    watermarkX,
    watermarkY,
    watermarkOpacity,
    watermarkSize,
    exportFormat,
    outputFileName,
    quality,
    outputResizeEnabled,
    outputSize,
    optimizeDimensions,
    customRatioWidth,
    customRatioHeight,
    historyIndex,
    history,
    websiteLogoOptions,
    selectedWebsiteLogoId,
    originalWidth,
    originalHeight,
    viewportZoom,
    cropSelection,
    cropSelectionStyle,
    canvasDisplayStyle,
    canSaveCustomAspectRatio,
    editorFileName,
    isLocalFileEditor,
    aspectRatioOptions,
    exportFormatOptions,
    exportQualityDisabled,
    setViewportZoom,
    resetSelection,
    setCropMode,
    applyCustomAspectRatio,
    applyTemplate,
    rotateImage,
    applyTransform,
    saveHistory,
    startCrop,
    nudgeSelection,
    startMove,
    startResize,
    isWatermarkPositionPresetActive,
    setWatermarkType,
    onWebsiteLogoChange,
    enableWatermark,
    onWatermarkLogo,
    setWatermarkPosition,
    updateWatermarkControl,
    saveEditedImage,
    resetEditor,
    undo,
    redo
} = createCropperEditor(props, emit);

const busy = computed(() => loading.value || saving.value || backgroundRemovalLoading.value);
defineExpose({
    saveEditedImage,
    busy
});

function resetFilters()
{
    brightness.value = 100;
    contrast.value = 100;
    saturation.value = 100;
    grayscale.value = 0;

    saveHistory($t('[[[Reset filtrów]]]'));
}
</script>

<template>
    <div class="file-image-editor flex min-h-0 min-w-0 flex-col overflow-hidden bg-app-bg dark:bg-app-bg-dark" :class="dialog ? 'h-full' : 'h-[min(820px,calc(100dvh-160px))] min-h-[560px] rounded-[8px] border border-border dark:border-border-dark'" :aria-busy="busy">
        <div class="grid min-h-0 min-w-0 flex-1 grid-cols-[260px_minmax(0,1fr)_280px] gap-[16px] p-[16px] max-xl:grid-cols-2 max-xl:overflow-y-auto max-md:grid-cols-1">
            <fieldset class="m-0 min-h-0 min-w-0 overflow-y-auto border-0 p-0 [scrollbar-gutter:stable] max-xl:overflow-visible" :disabled="busy" :inert="busy || undefined">
        <CropperLeftSidebar
            v-model:crop-mode="cropMode"
            v-model:aspect-ratio="aspectRatio"
            v-model:fixed-width="fixedSize.width"
            v-model:fixed-height="fixedSize.height"
            v-model:circular-size="circularSize"
            v-model:custom-ratio-width="customRatioWidth"
            v-model:custom-ratio-height="customRatioHeight"
            v-model:flip-horizontal="flipHorizontal"
            v-model:flip-vertical="flipVertical"
            v-model:scale="scale"
            :templates="cropperTemplates"
            :aspect-ratio-options="aspectRatioOptions"
            :can-save-custom-aspect-ratio="canSaveCustomAspectRatio"
            @set-crop-mode="setCropMode"
            @reset-selection="resetSelection"
            @apply-custom-aspect-ratio="applyCustomAspectRatio"
            @apply-template="applyTemplate"
            @rotate-image="rotateImage"
            @apply-transform="applyTransform"
            @save-history="saveHistory"
        >
            <template #filters>
                <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="`${$t('[[[Jasność]]]')}: ${brightness}%`" :label-for="fieldId + '-brightness'">
                    <CropperRange :id="fieldId + '-brightness'"
                    v-model="brightness"
                    :aria-label="$t('[[[Jasność]]]')"
                    :min="0"
                    :max="200"
                    @change="saveHistory($t('[[[Zmiana filtrów]]]'))"
                />
                </IdeoFormGroup>
                <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="`${$t('[[[Kontrast]]]')}: ${contrast}%`" :label-for="fieldId + '-contrast'">
                    <CropperRange :id="fieldId + '-contrast'"
                    v-model="contrast"
                    :aria-label="$t('[[[Kontrast]]]')"
                    :min="0"
                    :max="200"
                    @change="saveHistory($t('[[[Zmiana filtrów]]]'))"
                />
                </IdeoFormGroup>
                <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="`${$t('[[[Nasycenie]]]')}: ${saturation}%`" :label-for="fieldId + '-saturation'">
                    <CropperRange :id="fieldId + '-saturation'"
                    v-model="saturation"
                    :aria-label="$t('[[[Nasycenie]]]')"
                    :min="0"
                    :max="200"
                    @change="saveHistory($t('[[[Zmiana filtrów]]]'))"
                />
                </IdeoFormGroup>
                <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="`${$t('[[[Skala szarości]]]')}: ${grayscale}%`" :label-for="fieldId + '-grayscale'">
                    <CropperRange :id="fieldId + '-grayscale'"
                    v-model="grayscale"
                    :aria-label="$t('[[[Skala szarości]]]')"
                    :min="0"
                    :max="100"
                    @change="saveHistory($t('[[[Zmiana filtrów]]]'))"
                />
                </IdeoFormGroup>
                <IdeoAction stop-propagation
                    variant="secondary"
                    class="mt-[8px] w-full"
                    :text="$t('[[[Reset filtrów]]]')"
                    @click="resetFilters"
                />
            </template>
        </CropperLeftSidebar>
            </fieldset>
        <CropperStage :controls-target="zoomTarget" class="max-xl:order-first max-xl:col-span-2 max-md:col-span-1"
            ref="cropperStage"
            v-model:viewport-zoom="viewportZoom"
            :loading="loading"
            :editor-file-name="editorFileName"
            :original-width="originalWidth"
            :original-height="originalHeight"
            :canvas-display-style="canvasDisplayStyle"
            :crop-selection-style="cropSelectionStyle"
            :crop-selection="cropSelection"
            @set-viewport-zoom="setViewportZoom"
            @start-crop="startCrop"
            @nudge-selection="nudgeSelection"
            @start-move="startMove"
            @start-resize="startResize"
        />
            <fieldset class="m-0 min-h-0 min-w-0 overflow-y-auto border-0 p-0 [scrollbar-gutter:stable] max-xl:overflow-visible" :disabled="busy" :inert="busy || undefined">
        <CropperRightSidebar embedded
            ref="cropperRightSidebar"
            v-model:watermark-enabled="watermarkEnabled"
            v-model:watermark-type="watermarkType"
            v-model:watermark-text="watermarkText"
            v-model:selected-website-logo-id="selectedWebsiteLogoId"
            v-model:watermark-x="watermarkX"
            v-model:watermark-y="watermarkY"
            v-model:watermark-opacity="watermarkOpacity"
            v-model:watermark-size="watermarkSize"
            v-model:export-format="exportFormat"
            v-model:output-file-name="outputFileName"
            v-model:quality="quality"
            v-model:output-resize-enabled="outputResizeEnabled"
            v-model:output-width="outputSize.width"
            v-model:output-height="outputSize.height"
            v-model:optimize-dimensions="optimizeDimensions"
            :crop-selection="cropSelection"
            :website-logo-options="websiteLogoOptions"
            :website-logo-loading="websiteLogoLoading"
            :watermark-position-presets="cropperWatermarkPositionPresets"
            :export-formats="exportFormatOptions"
            :is-local-file-editor="isLocalFileEditor"
            :export-quality-disabled="exportQualityDisabled"
            :dialog="dialog"
            :saving="saving"
            :background-removal-loading="backgroundRemovalLoading"
            :history-index="historyIndex"
            :history="history"
            :is-watermark-position-preset-active="isWatermarkPositionPresetActive"
            @save-history="saveHistory"
            @set-watermark-type="setWatermarkType"
            @website-logo-change="onWebsiteLogoChange"
            @enable-watermark="enableWatermark"
            @watermark-logo="onWatermarkLogo"
            @set-watermark-position="setWatermarkPosition"
            @update-watermark-control="updateWatermarkControl"
            @open-dialog="emit('open-dialog')"
            @remove-background="removeBackground"
            @save-edited-image="saveEditedImage"
            @reset-editor="resetEditor"
            @undo="undo"
            @redo="redo"
        />
            </fieldset>
        </div>
        <Portal :to="props.actionsTarget || 'file-editor-actions'" :disabled="!props.actionsTarget">
            <div class="flex w-full flex-wrap items-center gap-[8px]" :class="props.actionsTarget ? '' : 'shrink-0 border-t border-border px-[16px] py-[10px] dark:border-border-dark'">
                <IdeoAction variant="secondary" icon="undo-2" :text="$t('[[[Cofnij]]]')" :disabled="busy || historyIndex <= 0" :aria-label="$t('[[[Cofnij]]]')" :title="$t('[[[Cofnij]]]')" @click="undo" />
                <IdeoAction variant="secondary" icon="redo-2" :text="$t('[[[Ponów]]]')" :disabled="busy || historyIndex >= history.length - 1" :aria-label="$t('[[[Ponów]]]')" :title="$t('[[[Ponów]]]')" @click="redo" />
                <div class="mx-[4px] h-[20px] w-px bg-border dark:bg-border-dark" aria-hidden="true" />
                <PortalTarget :name="zoomTarget" />
                <span class="min-w-0 flex-1" />
                <IdeoAction variant="secondary" icon="rotate-ccw" :text="$t('[[[Reset]]]')" :disabled="busy" @click="resetEditor" />
                <AiActionButton v-if="!isLocalFileEditor" :required-features="['images']" variant="fancy" icon="sparkles" :text="$t('[[[Usuń tło]]]')" :loading="backgroundRemovalLoading" :disabled="busy" @click="removeBackground" />
                <IdeoAction :variant="isLocalFileEditor ? 'primary' : 'success'" icon="check" :text="isLocalFileEditor ? $t('[[[Zastosuj modyfikacje]]]') : $t('[[[Zapisz jako nowy]]]')" :loading="saving" :disabled="busy" @click="saveEditedImage" />
            </div>
        </Portal>
    </div>
</template>
